import { Component, Input, OnChanges, ViewEncapsulation } from '@angular/core';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { Color, Icons, LifecycleAware, TabsConfig } from '@lsq-frontend/common-ui';
import { BuyerAgingSummaryGql } from '@lsq-frontend/remote-services/financing-graphql';
import { I18n } from '@ngx-translate/i18n-polyfill';
import type { ChartOptions } from 'chart.js';
import { PopoverPlacement } from 'ngx-smart-popover';
import { BehaviorSubject, Observable } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

@LifecycleAware()
@Component({
    selector: 'aging-summary-scf',
    templateUrl: './aging-summary-scf.component.html',
    styleUrls: ['./aging-summary-scf.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AgingSummarySCFComponent implements OnChanges {
    @Input() public dataSourceAllInvoices!: LsqDataSource<BuyerAgingSummaryGql>;
    @Input() public dataSourcePayLaterOnly?: LsqDataSource<BuyerAgingSummaryGql>;
    @Input() public invoicesUrl!: string;

    private readonly _totalAgingLabel = this._i18n('Total Aging');
    private readonly _flexTermAgingLabel = this._i18n('FlexTerm Aging');
    public readonly chartOptions: ChartOptions = {
        cutoutPercentage: 60,
        aspectRatio: 1,
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
            enabled: false
        }
    };
    public readonly colors = ['#008661', '#5667b1', '#ffd146', '#ff5841'];
    public readonly color = Color;
    public readonly icons = Icons;
    public readonly blankChartColor = '#eef0f7';
    public readonly labels = [
        this._i18n({
            value: 'CURRENT',
            meaning: 'Open invoices due on or after today'
        }),
        this._i18n({
            value: '1-30 DAYS',
            meaning: 'Open invoices due 1-30 ago'
        }),
        this._i18n({
            value: '31-60 DAYS',
            meaning: 'Open invoices due 31-60 ago'
        }),
        this._i18n({
            value: '61+ DAYS',
            meaning: 'Open invoices due more than 60 days ago'
        })
    ];
    public readonly currentLabelTooltipPlacement = PopoverPlacement.Bottom;
    public readonly currentLabelTooltipTextTotal = this._i18n(
        'The sum of open invoices that have not reached their original due date yet.'
    );
    public readonly currentLabelTooltipTextFlexTerm = this._i18n(
        'The sum of open FlexTerm invoices that have not reached their new due date yet.'
    );
    public activeTab$ = new BehaviorSubject<string>(this._totalAgingLabel);
    public activeDataSource$ = this._getActiveDataSource$();
    public chartData$ = this._getChartData$();
    public chartIsZero$ = this._getChartIsZero$();
    public tabsConfig!: TabsConfig[];
    public currentLabelTooltipText$ = this.activeTab$.pipe(
        map((tab) =>
            tab === this._totalAgingLabel
                ? this.currentLabelTooltipTextTotal
                : this.currentLabelTooltipTextFlexTerm
        )
    );

    constructor(private _i18n: I18n) {}

    public ngOnChanges(): void {
        if (this.dataSourcePayLaterOnly) {
            this.tabsConfig = [
                { label: this._totalAgingLabel },
                { label: this._flexTermAgingLabel }
            ];
        } else {
            this.tabsConfig = [{ label: this._totalAgingLabel }];
        }

        this.activeDataSource$ = this._getActiveDataSource$();
        this.chartData$ = this._getChartData$();
        this.chartIsZero$ = this._getChartIsZero$();
    }

    private _getActiveDataSource$(): Observable<LsqDataSource<BuyerAgingSummaryGql>> {
        return this.activeTab$.pipe(
            map((activeTab) => {
                if (this.dataSourcePayLaterOnly) {
                    return activeTab === this._totalAgingLabel
                        ? this.dataSourceAllInvoices
                        : this.dataSourcePayLaterOnly;
                }
                return this.dataSourceAllInvoices;
            })
        );
    }

    private _getChartData$(): Observable<number[]> {
        return this.activeDataSource$.pipe(
            switchMap((dataSource) => dataSource.data$),
            map((agingData) => {
                const openAmountCurrentCents = agingData.openAmountCurrent.cents;
                const openAmountDayOneToThirtyCents = agingData.openAmountDayOneToThirty.cents;
                const openAmountDayThirtyOneToSixtyCents =
                    agingData.openAmountDayThirtyOneToSixty.cents;
                const openAmountDaySixtyOnePlusCents = agingData.openAmountDaySixtyOnePlus.cents;
                const openAmountTotalCents =
                    openAmountCurrentCents +
                    openAmountDayOneToThirtyCents +
                    openAmountDayThirtyOneToSixtyCents +
                    openAmountDaySixtyOnePlusCents;
                return [
                    isFinite(openAmountCurrentCents / openAmountTotalCents)
                        ? openAmountCurrentCents / openAmountTotalCents
                        : 0,
                    isFinite(openAmountDayOneToThirtyCents / openAmountTotalCents)
                        ? openAmountDayOneToThirtyCents / openAmountTotalCents
                        : 0,
                    isFinite(openAmountDayThirtyOneToSixtyCents / openAmountTotalCents)
                        ? openAmountDayThirtyOneToSixtyCents / openAmountTotalCents
                        : 0,
                    isFinite(openAmountDaySixtyOnePlusCents / openAmountTotalCents)
                        ? openAmountDaySixtyOnePlusCents / openAmountTotalCents
                        : 0
                ];
            })
        );
    }

    private _getChartIsZero$(): Observable<boolean> {
        return this.chartData$.pipe(
            map((data) => data.every((datum) => datum === 0)),
            delay(1) // Avoids ExpressionChangedAfterItHasBeenCheckedError
        );
    }
}
